import React from 'react';
import { IconButton, Link, Stack, Toolbar, Tooltip, Typography, useTheme } from '@mui/material';
import User from '../User';
import { DarkMode, LightMode, SupportAgent } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Link as RouterLink } from 'react-router';
import TOUDialog from '../TOUDialog';
import { callAPISync, updateCustomData } from '../../utils/api';
import { updateUserData } from '../../store/system/systemSlice';
import { useLogto } from '@logto/react';

const Header: React.FC = () => {
	const theme = useTheme();
	const userCustomData = useAppSelector((state) => state.system);
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const updateUserTheme = (newTheme: 'light' | 'default') => {
		callAPISync(logto, (token) => {
			updateCustomData(token, { theme: newTheme });
		});
	};

	const changeTheme = () => {
		if (userCustomData.theme === 'default') {
			updateUserTheme('light');
			dispatch(updateUserData({ theme: 'light' }));
		} else {
			updateUserTheme('default');
			dispatch(updateUserData({ theme: 'default' }));
		}
	};

	return (
		<>
			<Toolbar
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					padding: 1,
					boxShadow: `0 2px 4px ${theme.palette.divider}`,
				}}
			>
				{/* Logo and Name Section */}
				<Link component={RouterLink} to="/" underline="none" color="inherit">
					<Stack
						sx={{
							display: 'flex',
							alignItems: 'center',
						}}
						spacing={2}
						direction={'row'}
					>
						<img src="/logo.png" height="48px" alt="Prometheus Logo" />

						<Typography variant="h3" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
							Prometheus
						</Typography>
					</Stack>
				</Link>

				{/* User Section */}
				<Stack
					sx={{
						display: 'flex',
						alignItems: 'center',
					}}
					spacing={4}
					direction={'row'}
				>
					<Tooltip title={`Opens GLPI (support desk) in new tab`}>
						<IconButton
							size="large"
							href={`${import.meta.env.VITE_HELPDESK_URL}`}
							target="_blank"
							rel="noreferer"
							data-testid="header-glpi-button"
						>
							<SupportAgent />
						</IconButton>
					</Tooltip>
					<Tooltip title={`Change to ${userCustomData.theme === 'default' ? 'light' : 'dark'} mode`}>
						<IconButton
							size="large"
							onClick={() => changeTheme()}
							data-testid={`change-theme-to-${userCustomData.theme === 'default' ? 'light' : 'dark'}-button`}
						>
							{userCustomData.theme === 'default' ? <LightMode /> : <DarkMode />}
						</IconButton>
					</Tooltip>
					<Tooltip title="Open Kibana">
						<IconButton
							aria-label="Link to Kibana"
							size="large"
							href={`${import.meta.env.VITE_KIBANA_URL}/spaces/space_selector`}
							target="_blank"
							rel="noreferer"
						>
							<img src="/kibana.png" height={'24px'} style={{ paddingLeft: '3px', paddingRight: '3px' }} alt="Kibana Logo" />
						</IconButton>
					</Tooltip>
					<Stack
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
						}}
					>
						<User />
					</Stack>
				</Stack>
			</Toolbar>
			<TOUDialog />
		</>
	);
};

export default Header;
